import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { ChatRecipientSuggestion } from '../../contexts/SurfaceContext';
import { usePublishSurfaceChatContext } from '../../hooks/usePublishSurfaceChatContext';
import type { ControlBridgeSurfaceKey } from './deriveViewModel';
import { CONTROL_BRIDGE_SURFACES } from './deriveViewModel';
import type { ResourceFilter } from './filters';
import { resourceFilterLabel } from './queueHelpers';
import type { ControlBridgeQueueItem } from './deriveViewModel';
import { exoChatSelectedWork } from './exoPanel/exoPanelHelpers';

export function useControlBridgeSurfaceChatContext(input: {
  workspaceSlug: string;
  companyTitle: string;
  surface: ControlBridgeSurfaceKey;
  scope: string;
  scopeLabel: string;
  resource: ResourceFilter;
  queueItems: ControlBridgeQueueItem[];
  artifactItemRef?: string;
  resourceProfileRef?: string;
  selectedItem?: ControlBridgeQueueItem | null;
}) {
  const {
    pathname
  } = useLocation();
  const {
    workspaceSlug,
    companyTitle,
    surface,
    scope,
    scopeLabel,
    resource,
    queueItems,
    artifactItemRef,
    resourceProfileRef,
    selectedItem,
  } = input;

  const resourceLabel = resourceFilterLabel(resource, queueItems);
  const surfaceLabel = CONTROL_BRIDGE_SURFACES.find((s) => s.key === surface)?.label ?? surface;

  // Do not publish CB resource-filter people as chat recipients. Those tokens are
  // IntentML / company_employee refs (often already person:<uuid>), not users.id.
  // Person messaging uses the company user directory (fetchCompanyChatRecipients).
  const suggestedRecipients = useMemo((): ChatRecipientSuggestion[] => [], []);

  const publishedContext = useMemo(
    () => ({
      surfaceId: `control-bridge:${workspaceSlug}:${surface}:${scope}:${resource}`,
      surfaceLabel: `${companyTitle} · ${surfaceLabel}`,
      route: pathname,
      summary:
        resource === 'all' ? `${scopeLabel} · all resources` : `${scopeLabel} · ${resourceLabel}`,
      suggestedRecipients,
      metadata: {
        workspaceSlug,
        surface,
        scope,
        resource,
        ...(artifactItemRef ? { artifactItemRef } : {}),
        ...(resourceProfileRef ? { resource_profile_ref: resourceProfileRef } : {}),
        ...(selectedItem
          ? { selectedWork: exoChatSelectedWork(selectedItem) }
          : {}),
      },
    }),
    [
      artifactItemRef,
      companyTitle,
      pathname,
      resource,
      resourceLabel,
      resourceProfileRef,
      scope,
      scopeLabel,
      selectedItem,
      suggestedRecipients,
      surface,
      surfaceLabel,
      workspaceSlug,
    ]
  );

  usePublishSurfaceChatContext(publishedContext);
}
